import type {
  Contract,
  CostComparison,
  Membership,
  ProductShipmentPerk,
  Term,
  Tier,
} from '../models/membership.model';
import { ContractState, IntervalType, ProductShipmentPerkType } from '../models/membership.model';

export class MembershipHelper {
  static calculateCostComparison(tier: Tier): void {
    tier.terms.forEach((term) => {
      const annualCost =
        (term.convertedPrice * this.getAnnualMultiplier(term)) / term.intervalCount;
      term.costComparison = {
        annualCost,
      } as CostComparison;
    });
  }

  private static getAnnualMultiplier(term: Term): number {
    switch (term.intervalType) {
      case IntervalType.YEAR:
        return 1;
      case IntervalType.MONTH:
        return 12;
      case IntervalType.WEEK:
        return 52;
      case IntervalType.DAY:
        return 365;
      case IntervalType.HOUR:
        return 8760;
      case IntervalType.MINUTE:
        return 525600;
      default:
        return 0;
    }
  }

  static isTierCurrent(tier: Tier): boolean {
    return this.isTierActive(tier) || this.isTierFailed(tier);
  }

  static isTierActive(tier: Tier): boolean {
    return (
      tier?.contractState === ContractState.ACTIVE || tier?.contractState === ContractState.PENDING
    );
  }

  static isTierFailed(tier: Tier): boolean {
    return tier?.contractState === ContractState.PAYMENT_FAILED;
  }

  static isTierContractActive(tier: Tier, contract: Contract): boolean {
    return !!contract;
  }

  static isMembershipOrTierMaxMembersSpecified(membership: Membership, tier: Tier): boolean {
    return this.isMembershipMaxMembersSpecified(membership) || this.isTierMaxMembersSpecified(tier);
  }

  static isMembershipOrTierSoldOut(membership: Membership, tier: Tier): boolean {
    return this.isMembershipSoldOut(membership) || this.isTierSoldOut(tier);
  }

  static isMembershipMaxMembersSpecified(membership: Membership): boolean {
    return !!membership && !!membership.maxMembers && membership.maxMembers > 0;
  }

  static isMembershipCurrentMembersSpecified(membership: Membership): boolean {
    return !!membership && !!membership.currentMembers && membership.currentMembers > 0;
  }

  static isTierMaxMembersSpecified(tier: Tier): boolean {
    return !!tier && !!tier.maxMembers && tier.maxMembers > 0;
  }

  static isTierCurrentMembersSpecified(tier: Tier): boolean {
    return !!tier && !!tier.currentMembers && tier.currentMembers > 0;
  }

  static isTierSoldOut(tier: Tier): boolean {
    return (
      this.isTierMaxMembersSpecified(tier) &&
      this.isTierCurrentMembersSpecified(tier) &&
      tier.currentMembers >= tier.maxMembers
    );
  }

  static isMembershipSoldOut(membership: Membership): boolean {
    return (
      this.isMembershipMaxMembersSpecified(membership) &&
      this.isMembershipCurrentMembersSpecified(membership) &&
      membership.currentMembers >= membership.maxMembers
    );
  }

  static isContractFailed(contract: Contract): boolean {
    return contract?.state === ContractState.PAYMENT_FAILED;
  }

  static isContractFreeTier(contract: Contract): boolean {
    return contract?.tier?.freeTier;
  }

  static getActivePerks(
    memberships: Membership[],
    productId: string,
    productVariantId: string
  ): ProductShipmentPerk[] {
    if (!memberships || memberships.length === 0) {
      return [];
    }
    return memberships
      .flatMap((membership) => membership.tiers)
      .filter((tier) =>
        tier.terms
          .filter((term) => !term.perksDisabled)
          .flatMap((term) => term.associatedProductVariants)
          .some(
            (variant) =>
              variant.sourceProductId === productId && variant.sourceVariantId === productVariantId
          )
      )
      .flatMap((tier) => tier.perks);
  }

  static getActivePerksForPurchase(
    memberships: Membership[],
    productId: string,
    productVariantId: string
  ): ProductShipmentPerk[] {
    return this.getActivePerks(memberships, productId, productVariantId).filter(
      (perk) => perk.type !== ProductShipmentPerkType.AT_RENEWAL
    );
  }
}
